import { useConfirmDialog } from '../hooks/useConfirmDialog';
import React, { useEffect, useState, useMemo } from 'react';
import { getErrorMessage } from '@/lib/utils';
import { useParams, useNavigate } from 'react-router-dom';
import { useI18n } from '../contexts/I18nContext';
import { useSortableData } from '../hooks/useSortableData';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  ArrowLeft,
  Save,
  Plus,
  Trash2,
  Edit2,
  Copy,
  Users,
  Package,
  Euro,
  Loader2,
  Check,
  X
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CustomerPricelistPage = () => {
  const { relationshipId } = useParams();
  const navigate = useNavigate();
  const { t } = useI18n();
  const { confirm: confirmAction, ConfirmDialog } = useConfirmDialog();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [relationship, setRelationship] = useState(null);
  const [pricelist, setPricelist] = useState(null);
  const [products, setProducts] = useState([]);
  const [basePricelists, setBasePricelists] = useState([]);
  const [editingItem, setEditingItem] = useState(null);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [selectedBasePricelist, setSelectedBasePricelist] = useState('');
  
  // Form per editing
  const [editForm, setEditForm] = useState({
    price: 0,
    min_quantity: 1,
    notes: ''
  });
  
  // Form per aggiungere prodotto
  const [addForm, setAddForm] = useState({
    product_id: '',
    price: 0,
    min_quantity: 1,
    notes: ''
  });

  useEffect(() => {
    fetchData();
  }, [relationshipId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Carica relazione
      const relRes = await axios.get(`${API}/relationships`);
      const rel = relRes.data.find(r => r.id === relationshipId);
      setRelationship(rel);
      
      // Carica listino per questa relazione
      const plRes = await axios.get(`${API}/pricelists/for-relationship/${relationshipId}`);
      setPricelist(plRes.data);
      
      // Carica tutti i prodotti del seller
      const prodRes = await axios.get(`${API}/products`);
      setProducts(prodRes.data);
      
      // Carica listini base disponibili
      const baseRes = await axios.get(`${API}/pricelists`);
      setBasePricelists(baseRes.data);
      
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Errore nel caricamento dati');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCustomPricelist = async () => {
    if (!selectedBasePricelist) {
      toast.error('Seleziona un listino base');
      return;
    }
    
    try {
      setSaving(true);
      const res = await axios.post(
        `${API}/pricelists/${selectedBasePricelist}/duplicate-for-customer?relationship_id=${relationshipId}`
      );
      setPricelist(res.data);
      setShowCreateDialog(false);
      toast.success(t('pricelists.customCreated'));
    } catch (error) {
      toast.error(getErrorMessage(error, t('common.error')));
    } finally {
      setSaving(false);
    }
  };

  const handleEditItem = (item) => {
    setEditingItem(item);
    setEditForm({
      price: item.price,
      min_quantity: item.min_quantity || 1,
      notes: item.notes || ''
    });
  };

  const handleSaveItem = async () => {
    try {
      setSaving(true);
      await axios.put(
        `${API}/pricelists/${pricelist.id}/item/${editingItem.product_id}`,
        null,
        {
          params: {
            price: editForm.price,
            min_quantity: editForm.min_quantity,
            notes: editForm.notes || null
          }
        }
      );
      
      // Aggiorna localmente
      setPricelist(prev => ({
        ...prev,
        items: prev.items.map(item => 
          item.product_id === editingItem.product_id 
            ? { ...item, ...editForm }
            : item
        )
      }));
      
      setEditingItem(null);
      toast.success(t('pricelists.priceUpdated'));
    } catch (error) {
      toast.error(t('common.errorSaving'));
    } finally {
      setSaving(false);
    }
  };

  const handleAddProduct = async () => {
    if (!addForm.product_id || addForm.price <= 0) {
      toast.error(t('pricelists.enterValidPrice'));
      return;
    }
    
    try {
      setSaving(true);
      await axios.put(
        `${API}/pricelists/${pricelist.id}/item/${addForm.product_id}`,
        null,
        {
          params: {
            price: addForm.price,
            min_quantity: addForm.min_quantity,
            notes: addForm.notes || null
          }
        }
      );
      
      // Aggiorna localmente
      setPricelist(prev => ({
        ...prev,
        items: [...prev.items, {
          product_id: addForm.product_id,
          price: addForm.price,
          min_quantity: addForm.min_quantity,
          notes: addForm.notes
        }]
      }));
      
      setShowAddDialog(false);
      setAddForm({ product_id: '', price: 0, min_quantity: 1, notes: '' });
      toast.success(t('pricelists.productAdded'));
    } catch (error) {
      toast.error(t('common.error'));
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveProduct = async (productId) => {
    if (!await confirmAction({ title: 'Rimuovi prodotto', description: 'Rimuovere questo prodotto dal listino?', confirmText: 'Rimuovi', variant: 'destructive' })) return;
    
    try {
      await axios.delete(`${API}/pricelists/${pricelist.id}/item/${productId}`);
      
      setPricelist(prev => ({
        ...prev,
        items: prev.items.filter(item => item.product_id !== productId)
      }));
      
      toast.success(t('pricelists.productRemoved'));
    } catch (error) {
      toast.error(t('common.errorDeleting'));
    }
  };

  const getProductById = (productId) => {
    return products.find(p => p.id === productId);
  };

  // Prepara items con dati prodotto per l'ordinamento
  const itemsWithProductData = useMemo(() => {
    if (!pricelist?.items) return [];
    return pricelist.items.map(item => {
      const product = getProductById(item.product_id);
      return {
        ...item,
        product_name: product?.name || 'N/A',
        product_category: product?.category || '-'
      };
    });
  }, [pricelist?.items, products]);

  // Hook per ordinamento tabella listino
  const { sortedData: sortedItems, requestSort, getSortIcon, getSortClass } = useSortableData(
    itemsWithProductData, 
    { key: 'product_name', direction: 'asc' }
  );

  // Prodotti non ancora nel listino
  const availableProducts = products.filter(
    p => !pricelist?.items?.some(item => item.product_id === p.id)
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <ConfirmDialog />
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/relationships')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-heading font-bold">
              Listino per {relationship?.partner_company?.name || 'Cliente'}
            </h1>
            <p className="text-muted-foreground">
              Gestisci prezzi e prodotti personalizzati per questo cliente
            </p>
          </div>
        </div>
        
        {pricelist && !pricelist.is_standard && (
          <Badge variant="secondary" className="text-sm">
            <Users className="h-3 w-3 mr-1" />
            Listino Personalizzato
          </Badge>
        )}
      </div>

      {/* Info Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Package className="h-5 w-5" />
            {pricelist?.name || 'Listino Standard'}
          </CardTitle>
          <CardDescription>
            {pricelist?.is_standard 
              ? 'Questo cliente usa il listino standard. Crea un listino personalizzato per modificare prezzi e prodotti.'
              : `Listino personalizzato con ${pricelist?.items?.length || 0} prodotti`
            }
          </CardDescription>
        </CardHeader>
        <CardContent>
          {pricelist?.is_standard ? (
            <Button onClick={() => setShowCreateDialog(true)} data-testid="create-custom-pricelist-btn">
              <Copy className="h-4 w-4 mr-2" />
              Crea Listino Personalizzato
            </Button>
          ) : (
            <div className="flex gap-2">
              <Button onClick={() => setShowAddDialog(true)} data-testid="add-product-btn">
                <Plus className="h-4 w-4 mr-2" />
                Aggiungi Prodotto
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Products Table */}
      {sortedItems.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Prodotti nel Listino</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead 
                    className="cursor-pointer hover:bg-muted/50 select-none"
                    onClick={() => requestSort('product_name')}
                  >
                    <span className="flex items-center gap-1">
                      Prodotto
                      <span className={getSortClass('product_name')}>{getSortIcon('product_name')}</span>
                    </span>
                  </TableHead>
                  <TableHead 
                    className="cursor-pointer hover:bg-muted/50 select-none"
                    onClick={() => requestSort('product_category')}
                  >
                    <span className="flex items-center gap-1">
                      Categoria
                      <span className={getSortClass('product_category')}>{getSortIcon('product_category')}</span>
                    </span>
                  </TableHead>
                  <TableHead 
                    className="text-right cursor-pointer hover:bg-muted/50 select-none"
                    onClick={() => requestSort('price')}
                  >
                    <span className="flex items-center justify-end gap-1">
                      Prezzo
                      <span className={getSortClass('price')}>{getSortIcon('price')}</span>
                    </span>
                  </TableHead>
                  <TableHead 
                    className="text-right cursor-pointer hover:bg-muted/50 select-none"
                    onClick={() => requestSort('min_quantity')}
                  >
                    <span className="flex items-center justify-end gap-1">
                      Qta Min.
                      <span className={getSortClass('min_quantity')}>{getSortIcon('min_quantity')}</span>
                    </span>
                  </TableHead>
                  <TableHead>Note</TableHead>
                  <TableHead className="text-right">Azioni</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedItems.map((item) => {
                  const product = getProductById(item.product_id);
                  const isEditing = editingItem?.product_id === item.product_id;
                  
                  return (
                    <TableRow key={item.product_id}>
                      <TableCell className="font-medium">
                        {product?.name || 'Prodotto non trovato'}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{product?.category || '-'}</Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        {isEditing ? (
                          <Input
                            type="number"
                            step="0.01"
                            value={editForm.price}
                            onChange={(e) => setEditForm(prev => ({ ...prev, price: parseFloat(e.target.value) }))}
                            className="w-24 text-right"
                          />
                        ) : (
                          <span className="font-semibold text-primary">€{item.price.toFixed(2)}</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        {isEditing ? (
                          <Input
                            type="number"
                            value={editForm.min_quantity}
                            onChange={(e) => setEditForm(prev => ({ ...prev, min_quantity: parseInt(e.target.value) }))}
                            className="w-20 text-right"
                          />
                        ) : (
                          item.min_quantity || 1
                        )}
                      </TableCell>
                      <TableCell>
                        {isEditing ? (
                          <Input
                            value={editForm.notes}
                            onChange={(e) => setEditForm(prev => ({ ...prev, notes: e.target.value }))}
                            placeholder="Note..."
                            className="w-32"
                          />
                        ) : (
                          <span className="text-sm text-muted-foreground">{item.notes || '-'}</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        {isEditing ? (
                          <div className="flex justify-end gap-1">
                            <Button size="icon" variant="ghost" onClick={handleSaveItem} disabled={saving}>
                              <Check className="h-4 w-4 text-green-600" />
                            </Button>
                            <Button size="icon" variant="ghost" onClick={() => setEditingItem(null)}>
                              <X className="h-4 w-4 text-red-600" />
                            </Button>
                          </div>
                        ) : (
                          <div className="flex justify-end gap-1">
                            {!pricelist.is_standard && (
                              <>
                                <Button 
                                  size="icon" 
                                  variant="ghost" 
                                  onClick={() => handleEditItem(item)}
                                  data-testid={`edit-item-${item.product_id}`}
                                >
                                  <Edit2 className="h-4 w-4" />
                                </Button>
                                <Button 
                                  size="icon" 
                                  variant="ghost" 
                                  onClick={() => handleRemoveProduct(item.product_id)}
                                  data-testid={`remove-item-${item.product_id}`}
                                >
                                  <Trash2 className="h-4 w-4 text-red-500" />
                                </Button>
                              </>
                            )}
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Dialog Crea Listino Personalizzato */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Crea Listino Personalizzato</DialogTitle>
            <DialogDescription>
              Seleziona un listino base da cui partire. Potrai poi modificare prezzi e prodotti per questo cliente.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Listino Base</Label>
              <Select value={selectedBasePricelist} onValueChange={setSelectedBasePricelist}>
                <SelectTrigger>
                  <SelectValue placeholder="Seleziona listino base..." />
                </SelectTrigger>
                <SelectContent>
                  {basePricelists.map(pl => (
                    <SelectItem key={pl.id} value={pl.id}>
                      {pl.name} ({pl.items?.length || 0} prodotti)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
              Annulla
            </Button>
            <Button onClick={handleCreateCustomPricelist} disabled={saving || !selectedBasePricelist}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Copy className="h-4 w-4 mr-2" />}
              Crea Listino
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Aggiungi Prodotto */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Aggiungi Prodotto al Listino</DialogTitle>
            <DialogDescription>
              Seleziona un prodotto e imposta il prezzo personalizzato per questo cliente.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Prodotto</Label>
              <Select value={addForm.product_id} onValueChange={(v) => setAddForm(prev => ({ ...prev, product_id: v }))}>
                <SelectTrigger>
                  <SelectValue placeholder="Seleziona prodotto..." />
                </SelectTrigger>
                <SelectContent>
                  {availableProducts.map(p => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.name} - {p.category}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Prezzo (€)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={addForm.price}
                  onChange={(e) => setAddForm(prev => ({ ...prev, price: parseFloat(e.target.value) }))}
                />
              </div>
              <div className="space-y-2">
                <Label>Quantità Minima</Label>
                <Input
                  type="number"
                  value={addForm.min_quantity}
                  onChange={(e) => setAddForm(prev => ({ ...prev, min_quantity: parseInt(e.target.value) }))}
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Note (opzionale)</Label>
              <Input
                value={addForm.notes}
                onChange={(e) => setAddForm(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Note per questo prodotto..."
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>
              Annulla
            </Button>
            <Button onClick={handleAddProduct} disabled={saving || !addForm.product_id}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
              Aggiungi
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CustomerPricelistPage;
